import React, { useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { UserPicker } from 'components/collab/lab/LabDialogs';

/** Invite people to an event. The server decides who may be invited (connections, consortium partners,
 *  the members of the event's lab or consortium) and tells us who could not be reached. */
export default function InviteDialog({ open, onClose, event, onInvited }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setPicked([]); setError(null); setResult(null); } }, [open]);

  const add = (u) => { if (u && !picked.some((p) => p.id === u.id)) setPicked([...picked, u]); };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axiosService.post(`/events/${event.id}/invite/`, { user_ids: picked.map((p) => p.id) });
      setResult(data);
      setPicked([]);
      onInvited?.(data.event);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not send the invitations.'));
    } finally {
      setBusy(false);
    }
  };

  const groupHint = event?.lab_info
    ? `members of ${event.lab_info.name}`
    : event?.consortium_info ? `members of ${event.consortium_info.name}` : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invite people</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            You can invite your connections and people in your consortia{groupHint ? `, and the ${groupHint}` : ''}.
            They get a notification and an email.
          </Typography>
          <UserPicker label="Add a person" value={null} onChange={add} />
          {picked.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {picked.map((p) => (
                <Chip key={p.id} label={p.name} avatar={<Avatar src={p.avatar_url || undefined}>{p.name[0]}</Avatar>}
                  onDelete={() => setPicked(picked.filter((x) => x.id !== p.id))} />
              ))}
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.not_reachable?.length ? 'warning' : 'success'}>
              {result.invited} invitation{result.invited === 1 ? '' : 's'} sent.
              {result.already_answered > 0 && ` ${result.already_answered} had already answered.`}
              {result.not_reachable?.length > 0 && ` Connect first with: ${result.not_reachable.join(', ')}.`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={send} disabled={busy || picked.length === 0}>
          {busy ? 'Sending…' : `Invite${picked.length ? ` ${picked.length}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
